import { FC } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import { DropdownMenu } from '../ui/organisms/DropdownMenu';
import { useCandidateDownload } from '../../hooks/useCandidateDownload';
import { Candidate, JobOffer } from '../../models/job';
import Button from '../ui/atoms/Button';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import { useTranslation } from 'react-i18next';

interface CandidateDownloadDropdownProps {
  candidate: Candidate;
  job: JobOffer;
  canDownloadAssessment?: boolean;
  className?: string;
  iconSize?: number;
  /** GTM origin (data-origin) */
  origin?: string;
}

export const CandidateDownloadDropdown: FC<CandidateDownloadDropdownProps> = ({
  candidate,
  job,
  canDownloadAssessment = true,
  className = '',
  origin = 'candidate_actions',
}) => {
  const { t } = useTranslation('candidates');
  const { downloadCV, downloadAssessment, isDownloadingCV, isDownloadingAssessment } =
    useCandidateDownload(candidate, job);

  const cvTracking = createGTMTracking('DOWNLOAD_CV', { origin, target: 'cv' });
  const assessmentTracking = createGTMTracking('DOWNLOAD_ASSESSMENT', { origin, target: 'assessment' });

  // If only CV is allowed → single button
  if (!canDownloadAssessment) {
    return (
      <Button
        {...getGTMAttributes(cvTracking)}
        onClick={downloadCV}
        disabled={isDownloadingCV}
        loading={isDownloadingCV}
        type="icon"
        intent={origin === 'candidate_actions' ? 'muted' : 'secondary'}
        size={origin === 'candidate_actions' ? 'sm' : 'md'}
        icon={isDownloadingCV ? ( <Loader2 className={`animate-spin`}/> ) : ( <Download/> )}
        title={t('downloads.cv_tooltip')}
        className={className}
      />
    );
  }

  // CV + Assessment → dropdown
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <Button
          type="icon"
          intent={origin === 'candidate_actions' ? 'muted' : 'secondary'}
          size={origin === 'candidate_actions' ? 'sm' : 'md'}
          disabled={isDownloadingCV || isDownloadingAssessment}
          loading={isDownloadingCV || isDownloadingAssessment}
          icon={isDownloadingCV || isDownloadingAssessment ? ( <Loader2 className="animate-spin"/> ) : ( <Download/> )}
          title={t('downloads.documents_tooltip')}
          className={className}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        <DropdownMenu.Item
          {...getGTMAttributes(cvTracking)}
          onClick={downloadCV}
          disabled={isDownloadingCV}
        >
          <FileText className="h-4 w-4" />
          {t('downloads.cv')}
        </DropdownMenu.Item>
        <DropdownMenu.Item
          {...getGTMAttributes(assessmentTracking)}
          onClick={downloadAssessment}
          disabled={isDownloadingAssessment}
        >
          <FileText className="h-4 w-4" />
          {t('downloads.assessment_pdf')}
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};
